// src/services/invoiceService.js

import axiosInstance from '../config/axios.config';
import { toast } from 'react-toastify';

class InvoiceService {
    /**
     * Get all invoices for the current user
     * @param {Object} params - Query parameters for pagination and filtering
     * @returns {Promise} API response with invoices list
     */
    async getInvoices(params = {}) {
        try {
            const response = await axiosInstance.get('/invoices/paginated', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching invoices:', error);
            throw error;
        }
    }

    /**
     * Get specific invoice details
     * @param {string|number} invoiceId - Invoice ID
     * @returns {Promise} API response with invoice details
     */
    async getInvoice(invoiceId) {
        try {
            const response = await axiosInstance.get(`/invoices/${invoiceId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching invoice:', error);
            throw error;
        }
    }

    /**
     * Download invoice as PDF
     * @param {string|number} invoiceId - Invoice ID
     * @param {string} invoiceNumber - Invoice number for filename
     */
    async downloadInvoice(invoiceId, invoiceNumber = null) {
        try {
            const response = await axiosInstance.get(`/invoices/${invoiceId}/download`, {
                responseType: 'blob'
            });

            // Create filename - replace forward slashes with hyphens for safe filename
            const safeInvoiceNumber = invoiceNumber ? invoiceNumber.replace(/\//g, '-') : invoiceId;
            const filename = `invoice-${safeInvoiceNumber}.pdf`;

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data], { 
                type: 'application/pdf' 
            }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            
            // Trigger download
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            link.remove();
            window.URL.revokeObjectURL(url);

            // Show success toast
            toast.success(`Invoice ${invoiceNumber || invoiceId} downloaded successfully`);
            
        } catch (error) {
            console.error('Error downloading invoice:', error);
            toast.error('Failed to download invoice. Please try again.');
            throw error;
        }
    }

    /**
     * Format currency amount
     * @param {number} amount - Amount in paise
     * @returns {string} Formatted currency string
     */
    formatCurrency(amount) {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount / 100);
    }

    /**
     * Format date for display
     * @param {string} dateString - ISO date string
     * @returns {string} Formatted date string
     */
    formatDate(dateString) {
        return new Intl.DateTimeFormat('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        }).format(new Date(dateString));
    }

    /**
     * Get status badge severity for PrimeReact Tag
     * @param {string} status - Invoice status
     * @returns {string} Severity level for Tag component
     */
    getStatusSeverity(status) {
        switch (status?.toLowerCase()) {
            case 'paid':
                return 'success';
            case 'pending':
                return 'warning';
            case 'cancelled':
            case 'failed':
                return 'danger';
            default:
                return 'info';
        }
    }

    /**
     * Get invoice type description
     * @param {Object} invoice - Invoice object
     * @returns {string} Human-readable description
     */
    getInvoiceDescription(invoice) {
        if (invoice.order?.credits) {
            return `Credit Recharge - ${invoice.order.credits} credits`;
        }
        return 'Credit Recharge';
    }

    // ==================== ADMIN METHODS ====================

    /**
     * Get all invoices for tenant admin with advanced filtering
     * @param {Object} params - Query parameters for pagination and filtering
     * @returns {Promise} API response with invoices list
     */
    async getAdminInvoices(params = {}) {
        try {
            const response = await axiosInstance.get('/admin/invoices', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching admin invoices:', error);
            throw error;
        }
    }

    /**
     * Get companies for filter dropdown (Admin only)
     * Uses existing api/admin/companies endpoint (CompaniesController)
     * @param {Object} params - Optional search/pagination params
     * @returns {Promise} API response with companies list
     */
    async getCompaniesForFilter(params = {}) {
        try {
            const response = await axiosInstance.get('/admin/companies', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching companies:', error);
            throw error;
        }
    }

    /**
     * Get users for filter dropdown (Admin only)
     * Uses existing api/users endpoint (UserLookupController)
     * @param {Object} params - Optional search/filter params
     * @returns {Promise} API response with users list
     */
    async getUsersForFilter(params = {}) {
        try {
            const response = await axiosInstance.get('/users', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching users:', error);
            throw error;
        }
    }

    /**
     * Get invoice metrics (This Month, Quarter, Half Year, This Year)
     * Optimized: Returns all metrics in ONE API call
     * @returns {Promise} API response with metrics data
     */
    async getInvoiceMetrics() {
        try {
            const response = await axiosInstance.get('/admin/invoices/metrics');
            return response.data;
        } catch (error) {
            console.error('Error fetching invoice metrics:', error);
            throw error;
        }
    }

    /**
     * Download invoice as PDF (Admin - can download any invoice in tenant)
     * @param {string|number} invoiceId - Invoice ID
     * @param {string} invoiceNumber - Invoice number for filename
     */
    async adminDownloadInvoice(invoiceId, invoiceNumber = null) {
        try {
            const response = await axiosInstance.get(`/admin/invoices/${invoiceId}/download`, {
                responseType: 'blob'
            });

            // Create filename - replace forward slashes with hyphens for safe filename
            const safeInvoiceNumber = invoiceNumber ? invoiceNumber.replace(/\//g, '-') : invoiceId;
            const filename = `invoice-${safeInvoiceNumber}.pdf`;

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data], {
                type: 'application/pdf'
            }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);

            // Trigger download
            document.body.appendChild(link);
            link.click();

            // Cleanup
            link.remove();
            window.URL.revokeObjectURL(url);

            // Show success toast
            toast.success(`Invoice ${invoiceNumber || invoiceId} downloaded successfully`);

        } catch (error) {
            console.error('Error downloading invoice:', error);
            toast.error('Failed to download invoice. Please try again.');
            throw error;
        }
    }

    // ==================== COMPANY ADMIN METHODS ====================

    /**
     * Get all invoices for company admin (company-scoped)
     * @param {Object} params - Query parameters for pagination and filtering
     * @returns {Promise} API response with invoices list
     */
    async getCompanyInvoices(params = {}) {
        try {
            const response = await axiosInstance.get('/company/invoices', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching company invoices:', error);
            throw error;
        }
    }

    /**
     * Get company users for filter dropdown (Company Admin)
     * @param {Object} params - Optional search/filter params
     * @returns {Promise} API response with users list
     */
    async getCompanyUsersForFilter(params = {}) {
        try {
            const response = await axiosInstance.get('/company/users', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching company users:', error);
            throw error;
        }
    }

    /**
     * Get invoice metrics for company (This Month, Quarter, Half Year, This Year)
     * @returns {Promise} API response with metrics data
     */
    async getCompanyInvoiceMetrics() {
        try {
            const response = await axiosInstance.get('/company/invoices/metrics');
            return response.data;
        } catch (error) {
            console.error('Error fetching company invoice metrics:', error);
            throw error;
        }
    }

    /**
     * Download invoice as PDF (Company Admin - can download any invoice in company)
     * @param {string|number} invoiceId - Invoice ID
     * @param {string} invoiceNumber - Invoice number for filename
     */
    async companyDownloadInvoice(invoiceId, invoiceNumber = null) {
        try {
            const response = await axiosInstance.get(`/company/invoices/${invoiceId}/download`, {
                responseType: 'blob'
            });

            const safeInvoiceNumber = invoiceNumber ? invoiceNumber.replace(/\//g, '-') : invoiceId;
            const filename = `invoice-${safeInvoiceNumber}.pdf`;

            // Create blob and download
            const url = window.URL.createObjectURL(new Blob([response.data], {
                type: 'application/pdf'
            }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);

            document.body.appendChild(link);
            link.click();

            // Cleanup
            link.remove();
            window.URL.revokeObjectURL(url);

            // Show success toast
            toast.success(`Invoice ${invoiceNumber || invoiceId} downloaded successfully`);

        } catch (error) {
            console.error('Error downloading invoice:', error);
            toast.error('Failed to download invoice. Please try again.');
            throw error;
        }
    }
}

export default new InvoiceService();